import React, { useState } from "react";
import { Info, RotateCw } from "lucide-react";
import {
  Button,
  Modal,
  RichTextContent,
  RichTextEditor,
  SegmentedControl,
} from "../../../components/ui";
import { isRichTextEmpty, sameRichText } from "../../../lib/richText";
import type {
  PolicyDocumentId,
  PolicyDocuments,
  StoredPolicyDocuments,
} from "../types/brand.types";

/**
 * What the tab says about each of the two documents: the words over it, the
 * line an empty one is read back as - and written over - and the name the
 * screen reader calls it by.
 */
const DOCUMENTS: Array<{
  id: PolicyDocumentId;
  label: string;
  heading: string;
  placeholder: string;
}> = [
  {
    id: "guidelines",
    label: "Guidelines",
    heading: "Your guidelines",
    placeholder:
      "Write the guidelines a client should follow on any of your plans - meal timing, water, what to avoid…",
  },
  {
    id: "policy",
    label: "Policy",
    heading: "Your policy",
    placeholder:
      "Write the terms your plans are sold under - how a plan is shared, how to reach you, what a plan's validity is…",
  },
];

/** Both documents shut - what a save that landed leaves behind. */
const NONE_OPEN: Record<PolicyDocumentId, boolean> = {
  guidelines: false,
  policy: false,
};

interface BrandPoliciesTabProps {
  /** The two documents as they are being written - what the editor shows. */
  documents: PolicyDocuments;
  /**
   * The two as stored - what Cancel puts back and Save compares against,
   * and where the version and the client's rendering of each come from.
   */
  stored: StoredPolicyDocuments;
  onChange: (patch: Partial<PolicyDocuments>) => void;
  /**
   * Publishes whichever document is off its stored copy, and answers with
   * whether the whole of it landed - which is what closes the editors.
   */
  onSave: () => Promise<boolean>;
  /** Puts one document back on its stored copy - the editor's Cancel. */
  onCancel: (id: PolicyDocumentId) => void;
  /**
   * Puts the system default back on one document - on the server, at once.
   * The stored copy and the box both follow what comes back.
   */
  onReset: (id: PolicyDocumentId) => void;
  saving: boolean;
  resetting: boolean;
}

/**
 * My brand → Guidelines & policy: the two pieces of writing a plan carries
 * with it, each read back as it stands and opened for writing by its Edit.
 *
 * One document at a time, switched by the pill row at the top rather than two
 * of each: they are the same kind of thing written in the same way, and the
 * switch is what says which is open.
 *
 * At rest each is the stored document read back - the server's rendering of
 * it, clause tokens already replaced, which is the text a client gets. Edit
 * is the way into the box, the way `BrandOverview` is the way into the
 * Details form and for the same reasons: the common visit is somebody
 * checking what their plans say, nothing can be changed by a stray keystroke
 * on the way past, and the foot of a phone stays the app's tab bar rather
 * than a Save row for a form nobody opened.
 *
 * The column is capped at 70% of the tab from `lg` up. A document set across
 * the whole of a wide screen runs to well over a hundred characters a line,
 * which is a line the eye loses its place on; the cap is what keeps it
 * readable now that nothing stands beside it to take the rest.
 *
 * The drafts live on the page, the way the Details form's do, so a switch to
 * another tab and back opens on what was being written rather than on the
 * stored copy - and `editing` is seeded from them below, so a document left
 * half-written comes back in its box rather than as a read-back screen with
 * unsaved work hidden behind an Edit button.
 */
const BrandPoliciesTab: React.FC<BrandPoliciesTabProps> = ({
  documents,
  stored,
  onChange,
  onSave,
  onCancel,
  onReset,
  saving,
  resetting,
}) => {
  const [active, setActive] = useState<PolicyDocumentId>("guidelines");
  const [confirmingReset, setConfirmingReset] = useState(false);

  // Which of the two are open for writing. Seeded from the drafts rather than
  // shut: this tab is unmounted by a switch to another one and its state goes
  // with it, while the drafts do not, so a document that is off its stored
  // copy has to open back in the box it was being typed into.
  const [editing, setEditing] = useState<Record<PolicyDocumentId, boolean>>(
    () => ({
      guidelines: !sameRichText(documents.guidelines, stored.guidelines.body),
      policy: !sameRichText(documents.policy, stored.policy.body),
    })
  );

  const current = DOCUMENTS.find((document) => document.id === active)!;
  const html = documents[active];
  const record = stored[active];
  const open = editing[active];

  const activeDirty = !sameRichText(html, record.body);

  // A reset has something to do while the document is a published one, or
  // while the box has moved off the default it is on. On the default and
  // untouched, there is nothing to put back.
  const canReset = record.isCustom || (open && activeDirty);

  // What a client reads. The server writes it - the policy's clause tokens
  // replaced by their sentences - so the read-back screen shows the server's
  // rendering, as given, rather than the body as typed.
  const readingHtml = record.resolvedBody;

  const busy = saving || resetting;

  const edit = () => setEditing((state) => ({ ...state, [active]: true }));

  const cancel = () => {
    onCancel(active);
    setEditing((state) => ({ ...state, [active]: false }));
  };

  // Both close, not just this one: the Save row publishes whichever document
  // is off its stored copy, so a save that landed has left nothing unsaved
  // behind the other pill either.
  const save = async () => {
    if (await onSave()) setEditing(NONE_OPEN);
  };

  const reset = () => {
    setConfirmingReset(false);
    onReset(active);
  };

  return (
    // From `lg` up the tab owns what is left of the viewport, the way the
    // Form tab does, so the document scrolls inside its own card rather than
    // growing the page.
    <div className="flex flex-col lg:h-full lg:min-h-0">
      <SegmentedControl
        items={DOCUMENTS}
        value={active}
        onChange={setActive}
        label="Document"
        className="flex-shrink-0"
      />

      {/* The one column, capped from `lg` up - see the note above. Below it
          the phone has the full width, which at that size is a readable
          measure already. */}
      <div className="mt-4 flex flex-col lg:min-h-0 lg:flex-1 lg:w-[70%]">
        <div className="mb-3 flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] 3xl:text-[16px] text-ink-muted">
              {current.heading}
            </h2>
            {/* Which of the two this is: the text Naurist hands out, or the
                practice's own, and how many times it has been published.
                `is_custom: false` and `version: 0` come together - there is
                no version 0 row. */}
            <span className="rounded-full bg-[#F1F3F5] px-2 py-0.5 text-[11px] 3xl:text-[12px] font-medium text-ink-muted">
              {record.isCustom
                ? `Version ${record.version} · Customised`
                : "Naurist default"}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setConfirmingReset(true)}
              disabled={!canReset || busy}
              className="flex items-center gap-1.5 text-[13px] 3xl:text-[14px] text-ink-muted transition-colors hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ink-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded"
            >
              <RotateCw aria-hidden="true" className="h-4 w-4" />
              {resetting ? "Resetting…" : "Reset to default text"}
            </button>
            {/* The way in, and the only one: while the box is open its own
                Save row is what closes it again. */}
            {!open && (
              <Button size="sm" onClick={edit} disabled={busy}>
                Edit {current.label.toLowerCase()}
              </Button>
            )}
          </div>
        </div>

        {open ? (
          <RichTextEditor
            // Keyed on the document, so a switch is a fresh box: the other
            // document's undo history and caret have nothing to do with this
            // one, and the browser's own undo stack would otherwise carry
            // across.
            key={active}
            label={current.label}
            value={html}
            onChange={(next) => onChange({ [active]: next })}
            placeholder={current.placeholder}
            className="lg:min-h-0 lg:flex-1"
            contentClassName="min-h-[360px] lg:min-h-0"
            footer={
              // Pinned under the text it saves, the way the Details form's
              // row is pinned under its sections.
              <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
                <p className="flex items-center gap-1.5 text-[12px] 3xl:text-[13px] text-ink-subtle">
                  <Info aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
                  Saved changes affect only future plans.
                </p>
                <div className="flex items-center gap-3">
                  {/* Enabled with nothing typed, unlike Save: it is the way
                      back out of a box that was opened and not written in. */}
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={cancel}
                    disabled={busy}
                    className="min-w-[100px]"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="md"
                    onClick={() => void save()}
                    disabled={!activeDirty || busy}
                    className="min-w-[130px]"
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            }
          />
        ) : (
          // The document at rest. A named region rather than a bare box: it is
          // what the tab is about, and naming it is what lets a screen reader
          // jump to it.
          <section
            aria-label={`${current.label} document`}
            className="rounded-md border border-line bg-white lg:min-h-0 lg:flex-1 lg:overflow-y-auto custom-scrollbar-overlay"
          >
            {/* The editor's own page margins, to the pixel: Edit should put a
                toolbar and a Save row around the document, not move a word of
                it sideways. */}
            <div className="px-5 py-5 text-ink md:px-12 md:py-6">
              {isRichTextEmpty(readingHtml) ? (
                // Nothing written and nothing defaulted to: the same line the
                // empty box is written over, so the card says what belongs
                // here rather than standing blank.
                <p className="text-[14px] 3xl:text-[15px] leading-[1.6] text-ink-subtle">
                  {current.placeholder}
                </p>
              ) : (
                <RichTextContent html={readingHtml} />
              )}
            </div>
          </section>
        )}
      </div>

      <Modal
        open={confirmingReset}
        title="Reset to default text"
        hideTitle
        onClose={() => setConfirmingReset(false)}
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setConfirmingReset(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={reset}>
              Reset
            </Button>
          </>
        }
      >
        {/* Heading and rule drawn inset to the panel's padding, the way the
            unsaved-changes prompt draws its own. Hidden from screen readers:
            the modal's `h2` already carries the name. */}
        <h3
          aria-hidden="true"
          className="text-[20px] 3xl:text-[22px] font-medium text-ink"
        >
          Reset to default text
        </h3>
        <p className="mt-4 text-[14px] 3xl:text-[15px] leading-[1.6] text-ink-muted">
          This will replace your current {current.label.toLowerCase()} with
          the default Naurist {current.label.toLowerCase()}, right away - it
          goes on every plan created from now on. Any custom changes you’ve
          made will be removed.
        </p>
        <div className="mt-4 border-t border-line" />
      </Modal>
    </div>
  );
};

export default BrandPoliciesTab;
